// In-world 3D overlays: a selection ring under each selected unit/building, and a
// translucent build-placement ghost (green = buildable, red = not) at the hovered
// hex. Pure view: reads GameState + the shared ghostValid rule, mutates only its own
// scene objects. Owner: client-render3d.

import {
  BufferAttribute, BufferGeometry, DoubleSide, Line, LineDashedMaterial, Mesh,
  MeshBasicMaterial, RingGeometry, Scene,
} from 'three'
import { buildingFootprint } from '../../shared/constants'
import { CHUNK, chunkKey, hexToPixel } from '../../shared/hex'
import { HEX_SIZE, type GameState } from '../state'
import { ghostHexOk, ghostValid } from '../render/renderer'
import { elevAt } from '../render/terrain'
import { townhallHexHint } from '../../shared/elevation'
import { resolvePier } from '../pier'
import { CORNER, DOME, tileHeight } from './terrainMesh'
import type { UnitVis } from './entities3d'
import { buildingGeometry } from './models3d/buildings3d'
import { MeshBuilder } from './models3d/mesh'
import { MAT } from './models3d/palette3d'

const S = HEX_SIZE

export class Overlays3d {
  private ringGeo = ((): RingGeometry => { const g = new RingGeometry(0.82, 1, 30); g.rotateX(-Math.PI / 2); return g })()
  private ringMat = new MeshBasicMaterial({ color: 0x9ff2ff, transparent: true, opacity: 0.9, depthWrite: false })
  private rings: Mesh[] = []
  private ghost: Mesh | null = null
  private ghostKey = ''
  private ghostMat = new MeshBasicMaterial({ transparent: true, opacity: 0.42, depthWrite: false })
  private flag: Mesh | null = null
  private flagKey = ''
  private flagMat = new MeshBasicMaterial({ vertexColors: true })
  // Dashed route lines for selected own units (wpath is owner-only on the wire).
  private pathPool: Line[] = []
  private pathMat = new LineDashedMaterial({
    color: 0x9ff2ff, transparent: true, opacity: 0.8, depthWrite: false, dashSize: 5, gapSize: 4,
  })

  // Build-ghost footprint fills: one crowned hex tile per cell the placement will
  // consume — green where that cell is acceptable, red where it's the blocker.
  private fillPool: Mesh[] = []
  private fillGreen = new MeshBasicMaterial({ color: 0x5fd46a, transparent: true, opacity: 0.34, depthWrite: false, side: DoubleSide })
  private fillRed = new MeshBasicMaterial({ color: 0xe0503a, transparent: true, opacity: 0.38, depthWrite: false, side: DoubleSide })
  // Amber = "will be removed from the chop queue" (the Chop tool's Remove sub-mode).
  private fillAmber = new MeshBasicMaterial({ color: 0xf0a020, transparent: true, opacity: 0.4, depthWrite: false, side: DoubleSide })

  /** Crowned hex fan in tile-local space (matches the terrain top's dome). */
  private static fillGeometry(): BufferGeometry {
    const pos: number[] = []
    for (let e = 0; e < 6; e++) {
      const a = CORNER[e], b = CORNER[(e + 1) % 6]
      pos.push(0, DOME, 0, a.x, 0, a.z, b.x, 0, b.z)
    }
    const g = new BufferGeometry()
    g.setAttribute('position', new BufferAttribute(new Float32Array(pos), 3))
    return g
  }

  private fill(i: number): Mesh {
    let m = this.fillPool[i]
    if (!m) { m = new Mesh(Overlays3d.fillGeometry(), this.fillGreen); this.scene.add(m); this.fillPool[i] = m }
    m.visible = true
    return m
  }

  // Hovered-hex band: a thin pale border hugging the tile under the cursor.
  // DoubleSide: the hand-wound band triangles must read from above regardless of order.
  private hoverBand: Mesh | null = null
  private hoverBandMat = new MeshBasicMaterial({
    color: 0xe8f6ff, transparent: true, opacity: 0.5, depthWrite: false, side: DoubleSide,
  })

  // unitAnim: per-unit final world position written by EntityLayer3d each frame, so a
  // selected unit's ring rides exactly where its mesh stands (smoothing, melee closing).
  constructor(private scene: Scene, private unitAnim: Map<number, UnitVis>) {}

  /** Flat hex border band in tile-local space (outer edge at the hex corners). */
  private static bandGeometry(): BufferGeometry {
    const IN = 0.88
    const pos: number[] = []
    for (let e = 0; e < 6; e++) {
      const a = CORNER[e], b = CORNER[(e + 1) % 6]
      pos.push(
        a.x, 0, a.z, b.x, 0, b.z, b.x * IN, 0, b.z * IN,
        a.x, 0, a.z, b.x * IN, 0, b.z * IN, a.x * IN, 0, a.z * IN,
      )
    }
    const g = new BufferGeometry()
    g.setAttribute('position', new BufferAttribute(new Float32Array(pos), 3))
    return g
  }

  private ring(i: number): Mesh {
    let m = this.rings[i]
    if (!m) { m = new Mesh(this.ringGeo, this.ringMat); this.scene.add(m); this.rings[i] = m }
    m.visible = true
    return m
  }

  private pathLine(i: number): Line {
    let l = this.pathPool[i]
    if (!l) {
      l = new Line(new BufferGeometry(), this.pathMat)
      l.frustumCulled = false // rebuilt every frame; skip stale-bounds culling
      this.scene.add(l)
      this.pathPool[i] = l
    }
    l.visible = true
    return l
  }

  sync(st: GameState): void {
    let n = 0
    // selected units
    for (const id of st.selection.units) {
      const u = st.units.get(id); if (!u) continue
      const p = this.unitAnim.get(id)
      const c = hexToPixel(u.q, u.r, S) // fallback: logical hex, first frame only
      const m = this.ring(n++)
      m.position.set(p ? p.x : c.x, (p ? p.y : tileHeight(st, u.q, u.r)) + 0.6, p ? p.z : c.y)
      m.scale.set(13, 1, 13)
    }
    // selected building
    if (st.selection.building !== null) {
      const b = st.buildings.get(st.selection.building)
      if (b) {
        // ring centred on the footprint (matters for the farm's offset triangle)
        const foot = buildingFootprint(b.type, b.q, b.r, b.dir)
        let cx = 0, cz = 0
        for (const c of foot) { const fp = hexToPixel(c.q, c.r, S); cx += fp.x; cz += fp.y }
        cx /= foot.length; cz /= foot.length
        const rad = foot.length > 1 ? 46 : 28
        const m = this.ring(n++)
        m.position.set(cx, tileHeight(st, b.q, b.r) + 0.6, cz)
        m.scale.set(rad, 1, rad)
      }
    }
    // route lines: a dashed path from each selected own unit through its remaining
    // waypoints, with a small ring on the destination hex
    let np = 0
    for (const id of st.selection.units) {
      const u = st.units.get(id)
      if (!u || !u.wpath || u.wpath.length === 0) continue
      const pts: number[] = []
      const p0 = this.unitAnim.get(id)
      if (p0) pts.push(p0.x, p0.y + 1.6, p0.z)
      for (const h of u.wpath) {
        const hp = hexToPixel(h.q, h.r, S)
        pts.push(hp.x, tileHeight(st, h.q, h.r) + 1.6, hp.y)
      }
      if (pts.length < 6) continue
      const l = this.pathLine(np++)
      l.geometry.dispose()
      l.geometry = new BufferGeometry()
      l.geometry.setAttribute('position', new BufferAttribute(new Float32Array(pts), 3))
      l.computeLineDistances()
      const last = u.wpath[u.wpath.length - 1]
      const lp = hexToPixel(last.q, last.r, S)
      const m = this.ring(n++)
      m.position.set(lp.x, tileHeight(st, last.q, last.r) + 0.75, lp.y)
      m.scale.set(7, 1, 7)
    }
    for (let i = np; i < this.pathPool.length; i++) this.pathPool[i].visible = false
    for (let i = n; i < this.rings.length; i++) this.rings[i].visible = false

    this.syncRally(st)
    this.syncGhost(st)
    this.syncHoverHex(st)
  }

  /** Highlight the hex land under the cursor with a thin border band. */
  private syncHoverHex(st: GameState): void {
    const h = st.hover
    const loaded = h && st.chunks.has(chunkKey(Math.floor(h.q / CHUNK), Math.floor(h.r / CHUNK)))
    if (!loaded) { if (this.hoverBand) this.hoverBand.visible = false; return }
    if (!this.hoverBand) {
      this.hoverBand = new Mesh(Overlays3d.bandGeometry(), this.hoverBandMat)
      this.scene.add(this.hoverBand)
    }
    const p = hexToPixel(h.q, h.r, S)
    this.hoverBand.visible = true
    this.hoverBand.position.set(p.x, tileHeight(st, h.q, h.r) + 0.85, p.y)
  }

  private syncRally(st: GameState): void {
    const b = st.selection.building !== null ? st.buildings.get(st.selection.building) : null
    if (!b || b.owner !== st.me || !b.rally) { if (this.flag) this.flag.visible = false; return }
    const color = st.factions.get(b.owner)?.color ?? '#e6e2d8'
    if (this.flagKey !== color) {
      this.flagKey = color
      if (this.flag) this.scene.remove(this.flag)
      const mb = new MeshBuilder()
      mb.box(0, 15, 0, 1.6, 30, 1.6, MAT.woodDk) // pole
      mb.box(4, 25, 0, 8, 6, 0.7, color) // pennant
      this.flag = new Mesh(mb.geometry(), this.flagMat)
      this.scene.add(this.flag)
    }
    const p = hexToPixel(b.rally.q, b.rally.r, S)
    if (this.flag) { this.flag.visible = true; this.flag.position.set(p.x, tileHeight(st, b.rally.q, b.rally.r), p.y) }
  }

  private syncGhost(st: GameState): void {
    // Drag-paint preview: while a stroke is in progress, light up EVERY hex it covers
    // (green where the order can go, red where it can't — using each hex's cached
    // verdict, no per-frame revalidation). Committed on release; a back-drag trims the
    // tail. Checked first so it shows even if the cursor briefly leaves a tile, and it
    // supersedes the single-hover ghost.
    if (st.paintPath.length > 0 && st.paintType) {
      if (this.ghost) this.ghost.visible = false
      // A box order's Remove sub-mode paints its selection amber (unqueueing), not
      // green/red. chopCancel is only ever set while a box-order tool is armed.
      const remove = st.chopCancel
      let nf = 0
      for (const c of st.paintPath) {
        const m = this.fill(nf++)
        m.material = remove ? this.fillAmber : c.ok ? this.fillGreen : this.fillRed
        const p = hexToPixel(c.q, c.r, S)
        m.position.set(p.x, tileHeight(st, c.q, c.r) + 0.28, p.y)
      }
      for (let i = nf; i < this.fillPool.length; i++) this.fillPool[i].visible = false
      this.ghostKey = ''
      return
    }
    if (!st.buildMode || !st.hover) {
      if (this.ghost) this.ghost.visible = false
      for (const m of this.fillPool) m.visible = false
      this.ghostKey = ''
      return
    }
    const { q, r } = st.hover
    // Roads have no structure mesh — they're a flat tile flag. Instead of a
    // building ghost, tint the hovered hex green/red so the player sees exactly
    // where a click (or a drag-painted stroke) will lay road. Without this the
    // road tool gave no feedback at all in the 3D view.
    if (st.buildMode === 'road') {
      if (this.ghost) this.ghost.visible = false
      const m = this.fill(0)
      m.material = ghostValid(st, 'road', q, r) ? this.fillGreen : this.fillRed
      const p = hexToPixel(q, r, S)
      m.position.set(p.x, tileHeight(st, q, r) + 0.28, p.y)
      for (let i = 1; i < this.fillPool.length; i++) this.fillPool[i].visible = false
      this.ghostKey = ''
      return
    }
    const valid = ghostValid(st, st.buildMode, q, r)
    // footprint cells lit green/red — for the pier use its shoreline-snapped plan
    let cells: { q: number; r: number }[]
    const pierPlan = st.buildMode === 'pier' ? resolvePier(st, q, r, st.pierRotate) : null
    if (st.buildMode === 'pier') {
      cells = pierPlan ? buildingFootprint('pier', pierPlan.q, pierPlan.r, pierPlan.dir) : [{ q, r }]
    } else if (st.buildMode === 'farm') {
      cells = buildingFootprint('farm', q, r, st.pierRotate)
    } else {
      cells = buildingFootprint(st.buildMode, q, r)
    }
    // Each cell shows its own verdict: green if that hex is acceptable, red if it's
    // a blocker. The town hall folds in the shared majority-height hint (outlier
    // cells red) and also paints any too-steep rim hex outside the footprint.
    let cellOk = cells.map((c) => ghostHexOk(st, st.buildMode!, q, r, c.q, c.r))
    let ring: { q: number; r: number }[] = []
    if (st.buildMode === 'townhall') {
      // Town hall only: its rim rule is the one remaining ground blocker. Footprint
      // outliers are levelled by the site crew now (cells read green), and a farm's
      // neighbours never mattered — so no phantom red beside cliffs or dug pits.
      const hint = townhallHexHint(cells, (hq, hr) => elevAt(st, hq, hr))
      if (hint) {
        cellOk = cellOk.map((ok, i) => ok && hint.cellOk[i])
        ring = hint.steepRing
      }
    }
    let nf = 0
    for (let i = 0; i < cells.length; i++) {
      const c = cells[i]
      const m = this.fill(nf++)
      m.material = valid || cellOk[i] ? this.fillGreen : this.fillRed
      const p = hexToPixel(c.q, c.r, S)
      m.position.set(p.x, tileHeight(st, c.q, c.r) + 0.28, p.y)
    }
    for (const c of ring) {
      const m = this.fill(nf++)
      m.material = this.fillRed
      const p = hexToPixel(c.q, c.r, S)
      m.position.set(p.x, tileHeight(st, c.q, c.r) + 0.28, p.y)
    }
    for (let i = nf; i < this.fillPool.length; i++) this.fillPool[i].visible = false
    const key = `${st.buildMode}:${q}:${r}:${valid ? 1 : 0}:${st.buildMode === 'farm' ? ((st.pierRotate % 6) + 6) % 6 : 0}`
    if (key !== this.ghostKey) {
      this.ghostKey = key
      if (this.ghost) { this.scene.remove(this.ghost) }
      const geo = buildingGeometry(st.buildMode, '#cccccc', 1)
      this.ghost = new Mesh(geo, this.ghostMat)
      this.ghost.scale.set(1, 0.9, 1)
      this.scene.add(this.ghost)
    }
    this.ghostMat.color.set(valid ? 0x5fd46a : 0xe0503a)
    // The pier ghost sits on its shoreline-snapped land anchor and faces its water
    // deck, so R visibly re-aims the whole jetty — matching a placed pier's orient.
    const anchor = pierPlan ?? { q, r }
    const p = hexToPixel(anchor.q, anchor.r, S)
    if (this.ghost) {
      this.ghost.visible = true
      this.ghost.position.set(p.x, tileHeight(st, anchor.q, anchor.r), p.y)
      this.ghost.rotation.y =
        st.buildMode === 'farm' ? ((((st.pierRotate % 6) + 6) % 6) * Math.PI) / 3
        : pierPlan ? pierPlan.dir * (Math.PI / 3) - Math.PI / 2 // deck (-Z) → water hex DIRS[dir]
        : 0
    }
  }
}
